import { type ReactElement, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { usePortfolioCategoriesQuery, usePortfoliosQuery } from '@/entities/portfolio';
import { portfolioListPath } from '@/shared/config';
import { Button } from '@/shared/ui';
import { PortfolioCardGrid } from './portfolio-card-grid';
import { PortfolioCategoryTabs } from './portfolio-category-tabs';
import { PortfolioGridSkeleton } from './portfolio-grid-skeleton';
import { PortfolioPagination } from './portfolio-pagination';

interface PortfolioListProps {
  readonly categoryId?: string;
  readonly page: number;
  readonly invalidCategory?: boolean;
  readonly invalidPage?: boolean;
}

export const PortfolioList = ({
  categoryId,
  page,
  invalidCategory = false,
  invalidPage = false,
}: PortfolioListProps): ReactElement => {
  const navigate = useNavigate();
  const categoriesQuery = usePortfolioCategoriesQuery();
  const listQuery = usePortfoliosQuery(
    { categoryId, page },
    { enabled: !invalidCategory && !invalidPage },
  );
  const [notice, setNotice] = useState<string | null>(null);
  const handledCorrection = useRef<string | null>(null);
  const unknownCategory = categoryId !== undefined && listQuery.error?.data?.code === 'NOT_FOUND';
  const outsidePage = listQuery.data !== undefined && page > Math.max(1, listQuery.data.totalPages);
  const correction =
    invalidCategory || unknownCategory ? 'category' : invalidPage || outsidePage ? 'page' : null;

  useEffect(() => {
    if (correction === null) {
      handledCorrection.current = null;
      return;
    }
    const key = `${categoryId}:${page}:${correction}`;
    if (handledCorrection.current === key) return;
    handledCorrection.current = key;
    setNotice(
      correction === 'category'
        ? '존재하지 않는 카테고리입니다. 전체 목록으로 이동합니다.'
        : '페이지 범위를 벗어났습니다. 첫 페이지로 이동합니다.',
    );
    void navigate(
      portfolioListPath({ categoryId: correction === 'category' ? undefined : categoryId }),
      { replace: true },
    );
  }, [categoryId, page, correction, navigate]);

  const retry = () => {
    void listQuery.refetch();
    void categoriesQuery.refetch();
  };
  const listFailed = listQuery.isError && !unknownCategory;
  const categoriesFailed = categoriesQuery.isError;
  const hasData = listQuery.data !== undefined && !unknownCategory;
  return (
    <section className="space-y-10">
      <PortfolioCategoryTabs
        categories={categoriesQuery.data ?? []}
        categoryId={categoryId}
        onSelect={(next) => {
          setNotice(null);
          void navigate(portfolioListPath({ categoryId: next }), { replace: false });
        }}
      />
      {notice !== null && (
        <p role="status" className="text-sm text-muted-foreground">
          {notice}
        </p>
      )}
      {(listFailed || categoriesFailed) && (
        // 원문 오류는 화면에 싣지 않는다 — 사용자용 문장과 재시도만 남긴다.
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-4 border-y border-foreground/10 py-5"
        >
          <p className="text-sm text-destructive">
            {listFailed ? '포트폴리오를 불러오지 못했습니다.' : '카테고리를 불러오지 못했습니다.'}
          </p>
          <Button variant="outline" size="sm" onClick={retry}>
            다시 시도
          </Button>
        </div>
      )}
      {!hasData && !listFailed && correction === null && listQuery.isPending && (
        <PortfolioGridSkeleton />
      )}
      {hasData && correction === null && (
        <>
          {listQuery.data?.items.length === 0 ? (
            <p className="border-y border-foreground/10 py-20 text-center text-muted-foreground">
              {categoryId === undefined
                ? '등록된 포트폴리오가 없습니다.'
                : '해당 카테고리에 공개된 포트폴리오가 없습니다.'}
            </p>
          ) : (
            <PortfolioCardGrid
              items={listQuery.data?.items ?? []}
              featureFirst={page === 1}
              onRetry={() => {
                void listQuery.refetch();
              }}
            />
          )}
          <div className="pt-6">
            <PortfolioPagination
              categoryId={categoryId}
              page={page}
              totalPages={listQuery.data?.totalPages ?? 0}
            />
          </div>
        </>
      )}
    </section>
  );
};
